// Validation schema for the R-value (위험지수) threshold card. PRD 09 §3.4 (D-D2) + ticket
// SHCT-362 AC. Kept as a pure module so the rules can be unit-tested independently of the form.
// Messages are i18n keys resolved by the card. Rules:
//   - every value numeric, >= 0.0, one decimal place; NO upper bound (clarification ADM.024).
//   - each interval: lower limit < upper limit.
//   - adjacent continuity: the next interval starts ONE 0.1 step above the previous one's upper
//     limit (mid.min == low.max + 0.1, high.min == mid.max + 0.1) — no gap, no overlap.
//
// SHCT-681 — that last rule used to be written as strict equality (low.max == mid.min), which is how
// PRD §3.4's sentence reads literally. It cannot be what the spec means: the same §3.4 seeds the card
// with 0.0~5.9 / 6.0~14.4 / 14.5~26.8, values that fail equality, and with both limits inclusive an
// equal boundary would put 6.0 in two intervals at once — the overlap §3.4 forbids in the same breath.
// The server reads it the same way (BiometricService.adjacent: lowerMax + 0.1 == upperMin), so under
// the equality reading nothing the operator typed could ever be saved: whatever the card accepted, the
// server rejected, and vice versa. Product confirmed the step reading (2026-08-07).

import { z } from 'zod';

// One decimal place at most (PRD 09 §3.4 / ticket AC "one decimal place").
export const oneDecimal = (n: number) =>
  Math.abs(n * 10 - Math.round(n * 10)) < 1e-9;

/**
 * True when `upperMin` is exactly one 0.1 step above `lowerMax` — the boundary between two adjacent
 * intervals, with both limits inclusive. Compared in tenths because 5.9 + 0.1 is 5.999999999999999 in
 * binary floating point, which no direct comparison would call equal to 6.0.
 */
export function isNextStep(lowerMax: number, upperMin: number): boolean {
  return Math.round(upperMin * 10) - Math.round(lowerMax * 10) === 1;
}

function riskField() {
  return z
    .number({ error: 'riskRange.errors.required' })
    .min(0, 'riskRange.errors.minValue')
    .refine(oneDecimal, 'riskRange.errors.decimalPlaces');
}

const intervalSchema = z.object({ min: riskField(), max: riskField() });

export const riskRangeSchema = z
  .object({
    low: intervalSchema,
    mid: intervalSchema,
    high: intervalSchema,
  })
  .superRefine((v, ctx) => {
    // Only run interval/continuity checks once every field is a valid non-negative number;
    // otherwise the per-field errors above already carry the message.
    const nums = [
      v.low.min,
      v.low.max,
      v.mid.min,
      v.mid.max,
      v.high.min,
      v.high.max,
    ];
    if (!nums.every((n) => Number.isFinite(n) && n >= 0)) return;

    // Each interval: lower limit < upper limit.
    if (!(v.low.min < v.low.max)) {
      for (const b of ['min', 'max'] as const) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'riskRange.errors.lowOrder',
          path: ['low', b],
        });
      }
    }
    if (!(v.mid.min < v.mid.max)) {
      for (const b of ['min', 'max'] as const) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'riskRange.errors.midOrder',
          path: ['mid', b],
        });
      }
    }
    if (!(v.high.min < v.high.max)) {
      for (const b of ['min', 'max'] as const) {
        ctx.addIssue({
          code: z.ZodIssueCode.custom,
          message: 'riskRange.errors.highOrder',
          path: ['high', b],
        });
      }
    }

    // Adjacent continuity: the next interval starts one 0.1 step above the previous upper limit.
    if (!isNextStep(v.low.max, v.mid.min)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'riskRange.errors.continuityLowMid',
        path: ['low', 'max'],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'riskRange.errors.continuityLowMid',
        path: ['mid', 'min'],
      });
    }
    if (!isNextStep(v.mid.max, v.high.min)) {
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'riskRange.errors.continuityMidHigh',
        path: ['mid', 'max'],
      });
      ctx.addIssue({
        code: z.ZodIssueCode.custom,
        message: 'riskRange.errors.continuityMidHigh',
        path: ['high', 'min'],
      });
    }
  });
